/** Nearest-size BOM line copy helper for compose stubs (slice C). */
import { compareSizeLabels } from "./predicates";
import type { ComposeNode } from "./types";

export type RecipeLineSnapshot = {
  material_id?: string;
  component_recipe_id?: string;
  material_use_id?: string;
  quantity: number;
  role?: string;
  swappable?: boolean;
  upgrade_price_delta?: number | null;
  slot_label?: string;
};

/**
 * Among component nodes that share the same slotKey and are matched,
 * pick the closest size to `targetSize` (preferring equal or smaller footprint).
 */
export function findNearestMatchedSibling(
  target: ComposeNode,
  components: readonly ComposeNode[],
): ComposeNode | null {
  if (!target.sizeLabel || !target.slotKey) return null;
  const candidates = components.filter(
    (c) =>
      c.id !== target.id &&
      c.slotKey === target.slotKey &&
      c.sizeLabel &&
      c.match.status === "matched" &&
      (target.styleKey == null || c.styleKey === target.styleKey) &&
      (target.variantKey == null || c.variantKey === target.variantKey),
  );
  if (candidates.length === 0) return null;

  let best: ComposeNode | null = null;
  let bestDist = Number.POSITIVE_INFINITY;
  for (const c of candidates) {
    if (!c.sizeLabel) continue;
    const dist = Math.abs(compareSizeLabels(target.sizeLabel, c.sizeLabel));
    // Prefer same or smaller size when distances tie-break via compare
    const prefer =
      compareSizeLabels(c.sizeLabel, target.sizeLabel) <= 0 ? 0 : 0.5;
    const score = dist + prefer;
    if (score < bestDist) {
      bestDist = score;
      best = c;
    }
  }
  return best;
}

export function linesForDuplicateFromNearest(
  sourceLines: readonly RecipeLineSnapshot[],
): RecipeLineSnapshot[] {
  return sourceLines.map((l) => ({
    material_id: l.material_id,
    component_recipe_id: l.component_recipe_id,
    material_use_id: l.material_use_id,
    quantity: l.quantity,
    role: l.role,
    swappable: l.swappable,
    upgrade_price_delta: l.upgrade_price_delta,
    slot_label: l.slot_label,
  }));
}
